import {defineStore} from "pinia";
import posthog from "posthog-js";

// Disable flag controlled by environment (build-time): set MIX_POSTHOG_DISABLED=true to turn off all PostHog tracking
const __phDisabled = (() => {
	const v = String(process.env.MIX_POSTHOG_DISABLED || "").toLowerCase();
	return v === "true";
})();

// --- Internal helpers & state (module scope) ---
const LAST_KNOWN_FLAGS_KEY = "ph_last_known_flags";
let _flagsSubscribed = false;
let _readyCallbacks = [];
let _initialized = false;

function _loadLastKnown() {
	try {
		return JSON.parse(localStorage.getItem(LAST_KNOWN_FLAGS_KEY) || "{}");
	} catch (e) {
		return {};
	}
}

function _saveLastKnown(snapshot) {
	try {
		localStorage.setItem(LAST_KNOWN_FLAGS_KEY, JSON.stringify(snapshot));
	} catch (e) {
		// ignore
	}
}

function _snapshotFlags() {
	const keys = posthog.getFeatureFlagKeys?.() || [];
	const out = {};
	keys.forEach((k) => {
		out[k] = posthog.getFeatureFlag?.(k);
	});
	return out;
}

function _emitToggles(prev, next, trackEvent) {
	const allKeys = new Set([...Object.keys(prev || {}), ...Object.keys(next || {})]);
	allKeys.forEach((key) => {
		const before = prev?.[key];
		const after = next?.[key];
		if (before !== after) {
			try {
				trackEvent("app.feature.toggled", {
					feature_key: key,
					ui_variant: typeof after === "string" ? after : undefined,
					is_enabled: !!after,
				});
			} catch (_) {
			}
		}
	});
}

function _getContext() {
	const ctx = {
		ctx_environment: process.env.MIX_APP_ENV || "dev",
		ctx_app_url: process.env.MIX_APP_URL || "-",
		ctx_project: process.env.MIX_PROJECT || "-",
	};
	try {
		if (typeof window !== "undefined") {
			ctx.ctx_domain = window.location.hostname;
		}
	} catch (e) {
	}
	// Remove undefined to keep payload clean
	Object.keys(ctx).forEach((k) => ctx[k] === undefined && delete ctx[k]);
	return ctx;
}

// Compute a safe cookie domain for PostHog.
// Only return a domain for known parent domains; otherwise return undefined
// so the SDK defaults to the current hostname (avoids "invalid domain" errors in dev).
function _computePosthogCookieDomain() {
	try {
		if (typeof window === "undefined") return undefined;
		const host = String(window.location.hostname || "").toLowerCase();
		// Allowlist of parent domains where we want cross-subdomain cookies
		const allowed = [
			"energieausweis-erstellen-online.de",
			"immobilienwerker.de",
			"hausundenergie.de",
		];
		const match = allowed.find((d) => host === d || host.endsWith("." + d));
		return match ? "." + match : undefined;
	} catch (_) {
		return undefined;
	}
}

export const usePostHogStore = defineStore("posthogStore", {
	state: () => ({
		ready: false,
		flagsReady: false,
		distinctId: null,
		user: null,
		order: null,
		flags: {},
		overrides: {},
		featureFlags: {
			// Use string default to avoid type mismatches before flags are ready
			step1_email_required_or_optional: "control",
		},
	}),

	getters: {
		isReady: (s) => s.ready,
		isIdentified: (s) => !!(s.user && s.distinctId && (s.user?.uuid === s.distinctId || (s.distinctId && s.distinctId.length > 0))),
		currentOrderId: (s) => s.order?.uuid || s.order?.id || null,
	},

	persist: {
		enabled: false,
		strategies: [{key: "ph_flags", paths: ["flags", "flagsReady"]}],
	},

	actions: {
		displaySurvey(surveyId) {
			try {
				console.log("displaySurvey", surveyId);
				posthog.displaySurvey(surveyId, {
					displayType: 'popover', // oder 'inline'
					ignoreConditions: true,  // Bedingungen ignorieren
					ignoreDelay: true,       // Verzögerung ignorieren
				});
			} catch (e) {
			}
		},
		/** Initialize PostHog and feature flags; refresh distinctId. */
		init(options = {}) {
			try {
				// If disabled via env, do not initialize SDK; mark as ready and resolve any callbacks
				if (__phDisabled) {
					_initialized = true;
					this.flags = {...(this.flags || {}), ...(this.overrides || {})};
					this.flagsReady = true;
					this.ready = true;
					try {
						if (_readyCallbacks.length) {
							_readyCallbacks.forEach((cb) => {
								try {
									cb(this.flags);
								} catch (_) {
								}
							});
							_readyCallbacks = [];
						}
					} catch (_) {
					}
					return;
				}

				if (!_initialized) {
					posthog.init(
						"phc_V62vfbhtg4loh9FS3vhrTnyyPlT98r6MUlRSORpnm2w",
						{
							api_host: "https://eu.i.posthog.com",
							defaults: "2025-05-24",
							person_profiles: "identified_only",
							capture_pageview: true,
							disable_session_recording: false,
							// Set cookie_domain only for known parent domains. When undefined, SDK uses current host (safe for dev).
							cookie_domain: _computePosthogCookieDomain(),
							disable_surveys: true,
							...options,
						},
					);
					_initialized = true;
				}
				this.distinctId = this.getDistinctId();

				// Subscribe to feature flags once
				if (!_flagsSubscribed && typeof posthog.onFeatureFlags === "function") {
					_flagsSubscribed = true;
					const prev = _loadLastKnown();
					posthog.onFeatureFlags(() => {
						try {
							const next = _snapshotFlags();
							_emitToggles(prev, next, this.trackEvent);
							_saveLastKnown(next);
							// Merge SDK snapshot with local overrides (overrides win)
							this.flags = {...next, ...(this.overrides || {})};
						} catch (_) {
						}
						this.flagsReady = true;
						try {
							_readyCallbacks.forEach((cb) => {
								try {
									cb(this.flags);
								} catch (_) {
								}
							});
						} finally {
							_readyCallbacks = [];
						}
						try {
							this.distinctId = this.getDistinctId();
						} catch (_) {
						}
					});
				} else if (typeof posthog.onFeatureFlags !== "function") {
					this.flagsReady = true;
					try {
						_readyCallbacks.forEach((cb) => {
							try {
								cb(this.flags);
							} catch (_) {
							}
						});
					} finally {
						_readyCallbacks = [];
					}
				}

				this.ready = true;
			} catch (e) {
				this.ready = true;
			}
		},

		/** Send a PostHog event with standard context merged. */
		trackEvent(eventName, props = {}) {
			if (__phDisabled) return;
			const payload = {..._getContext(), ...props};
			try {
				posthog.capture(eventName, payload);
			} catch (_) {
			}
		},

		/** Convenience: page view event. */
		trackPageView(pageName, extra = {}) {
			this.trackEvent("app.page.viewed", {page_name: pageName, ...extra});
		},

		/** Identify the current user and set person properties according to plan. */
		identifyUser(user = {}, extraProps = {}) {
			const distinctId = user && user.id ? String("K" + user.id).trim() : "";
			if (!distinctId) {
				if (process.env.NODE_ENV !== "production") {
					// eslint-disable-next-line no-console
					console.warn("identifyUser called without a distinctId-capable user", user);
				}
				return;
			}
			const personProps = {
				user_uuid: user.uuid || null,
				user_type: typeof user.user_type !== "undefined" ? user.user_type : null,
				// orders_count: typeof user.orders_count === "number" ? user.orders_count : null,
				account_created_at: user.created_at ? new Date(user.created_at).toISOString() : null,
				...extraProps,
			};
			Object.keys(personProps).forEach((k) => personProps[k] === null && delete personProps[k]);
			try {
				posthog.identify(distinctId, personProps);
			} catch (_) {
			}
			this.user = {
				uuid: user.uuid || null,
				id: user.id ? "K" + user.id : null,
				user_type: user.user_type ?? null,
				// orders_count: user.orders_count ?? null,
				account_created_at: user.created_at ?? null,
			};
			try {
				this.distinctId = this.getDistinctId();
			} catch (_) {
			}
		},

		/** Incrementally update person properties. */
		setPersonProps(partial = {}) {
			try {
				posthog.setPersonProperties(partial);
			} catch (_) {
			}
		},

		/** Associate the active order (group) and upsert its properties. */
		setOrderGroup(order = {}) {
			if (!order || (!order.id && !order.uuid)) return;
			const groupType = "order";
			const groupId = String(order.orderId);
			try {
				posthog.group(groupType, groupId);
			} catch (_) {
			}
			const groupProps = {
				uuid: order.uuid || null,
				id: order.id,
				order_status: order.order_status || null,
				project: order.project || null,
				product: order?.energieausweis.product || null,
				order_total_price_eur:
					typeof order.order_total_price === "number"
						? order.order_total_price
						: typeof order.value_eur === "number"
							? order.value_eur
							: null,
				order_date: order.order_date
					? new Date(order.order_date).toISOString()
					: order.created_at
						? new Date(order.created_at).toISOString()
						: null,
				payment_method: order.payment_method || null,
			};
			Object.keys(groupProps).forEach((k) => groupProps[k] === null && delete groupProps[k]);
			try {
				if (typeof posthog.groupIdentify === "function") {
					posthog.groupIdentify(groupType, groupId, groupProps);
				} else {
					posthog.capture("$groupidentify", {
						$group_type: groupType,
						$group_key: groupId,
						$group_set: groupProps,
					});
				}
			} catch (_) {
			}
			this.order = {
				uuid: order.uuid || null,
				id: order.id || null,
				project: order.project || null,
				product: order?.energieausweis.product || null,
			};
		},

		/** Link order and user and emit canonical event. */
		linkOrderToUser(order, user, extraEventProps = {}) {
			if (user) this.identifyUser(user);
			if (order) this.setOrderGroup(order);
			console.log(order);
			console.log(user);
			this.trackEvent("order.linked_to_user", {
				uuid: order?.uuid,
				id: order?.id,
				orderId: order?.orderId,
				user_uuid: user?.uuid,
				user_id: user.id ? "K" + user.id : null,
				...extraEventProps,
			});
			try {
				this.distinctId = this.getDistinctId();
			} catch (_) {
			}
		},

		/** Reset identity (logout). */
		reset() {
			if (!_initialized) return;
			try {
				posthog.reset();
			} catch (_) {
			}
			this.user = null;
			this.order = null;
			this.distinctId = this.getDistinctId();
		},

		/** Get current distinct id (or null). */
		getDistinctId() {
			try {
				if (typeof posthog.get_distinct_id === "function") {
					return posthog.get_distinct_id();
				}
			} catch (_) {
			}
			return null;
		},

		/** Feature flags: helpers implemented locally */
		isFeatureEnabled(key, defaultValue = false) {
			const hasOverride = Object.prototype.hasOwnProperty.call(this.overrides || {}, key);
			const o = hasOverride ? this.overrides[key] : undefined;
			const val = hasOverride ? o : posthog.getFeatureFlag?.(key);
			if (typeof val === "boolean") return val;
			if (typeof val === "string") return true;
			return defaultValue;
		},
		getVariant(key, fallback = "control") {
			const hasOverride = Object.prototype.hasOwnProperty.call(this.overrides || {}, key);
			const o = hasOverride ? this.overrides[key] : undefined;
			const val = hasOverride ? o : posthog.getFeatureFlag?.(key);
			if (typeof val === "string") return val;
			if (val === true) return fallback;
			return fallback;
		},
		getVariantKey(key, fallback = "control") {
			return this.getVariant(key, fallback);
		},
		getVariantStrict(key) {
			const hasOverride = Object.prototype.hasOwnProperty.call(this.overrides || {}, key);
			const o = hasOverride ? this.overrides[key] : undefined;
			const val = hasOverride ? o : posthog.getFeatureFlag?.(key);
			return typeof val === "string" ? val : null;
		},
		isVariant(key, expected, options = {}) {
			const list = Array.isArray(expected) ? expected : [expected];
			const fallbackKey = options.fallbackKey || "control";
			const hasOverride = Object.prototype.hasOwnProperty.call(this.overrides || {}, key);
			const o = hasOverride ? this.overrides[key] : undefined;
			const val = hasOverride ? o : posthog.getFeatureFlag?.(key);
			if (typeof val === "string") return list.includes(val);
			if (val === true) return list.includes(fallbackKey);
			return false;
		},
		// --- Experiments convenience (aliases over feature flags) ---
		getExperimentVariant(key, fallback = "control") {
			return this.getVariant(key, fallback);
		},
		isExperimentVariant(key, expected, options = {}) {
			return this.isVariant(key, expected, options);
		},
		/** Track a goal/outcome for a given experiment flag, automatically attaching ui_variant */
		trackExperimentGoal(eventName, props = {}, {flagKey, attachUiVariant = true} = {}) {
			let payload = {...props};
			if (flagKey && attachUiVariant) {
				const v = this.getVariantStrict(flagKey) || this.getVariantKey(flagKey, "control");
				if (v) {
					payload = {...payload, ui_variant: v, experiment_key: flagKey};
				}
			}
			this.trackEvent(eventName, payload);
		},
		getFlagPayload(key, defaultPayload = null) {
			try {
				const payload = posthog.getFeatureFlagPayload?.(key);
				return typeof payload === "undefined" ? defaultPayload : payload;
			} catch (e) {
				return defaultPayload;
			}
		},
		reloadFeatureFlags() {
			if (typeof posthog.reloadFeatureFlags === "function") {
				try {
					posthog.reloadFeatureFlags();
				} catch (_) {
				}
			}
		},

		/** Locally override feature flags (accepts { key: value } or { flags: { key: value } }). */
		overrideFeatureFlags(overridesOrPayload = {}) {
			try {
				const map = overridesOrPayload && overridesOrPayload.flags && typeof overridesOrPayload.flags === "object"
					? overridesOrPayload.flags
					: overridesOrPayload;
				if (!map || typeof map !== "object") return;
				const prev = {...(this.flags || {})};
				// Store local overrides
				this.overrides = {...(this.overrides || {}), ...map};
				// Best-effort SDK override when available
				try {
					posthog.featureFlags?.override?.(map);
				} catch (_) {
				}
				// Merge into effective flags and mark ready
				this.flags = {...(this.flags || {}), ...map};
				this.flagsReady = true;
				_saveLastKnown(this.flags);
				_emitToggles(prev, this.flags, this.trackEvent);
				if (_readyCallbacks.length) {
					try {
						_readyCallbacks.forEach((cb) => {
							try {
								cb(this.flags);
							} catch (_) {
							}
						});
					} finally {
						_readyCallbacks = [];
					}
				}
			} catch (_) {
			}
		},

		/** Clear overrides. If keys provided (string or array), clear those; otherwise clear all. */
		clearOverrideFeatureFlags(keys) {
			try {
				if (!keys) {
					this.overrides = {};
					// Attempt to clear SDK overrides entirely
					try {
						posthog.featureFlags?.override?.(false);
					} catch (_) {
					}
				} else {
					const list = Array.isArray(keys) ? keys : [keys];
					const next = {...(this.overrides || {})};
					list.forEach((k) => {
						delete next[k];
					});
					this.overrides = next;
					// SDK: cannot partially clear via API reliably; ignore.
				}
				const prev = {...(this.flags || {})};
				const snap = _snapshotFlags();
				this.flags = {...snap, ...(this.overrides || {})};
				_saveLastKnown(this.flags);
				_emitToggles(prev, this.flags, this.trackEvent);
			} catch (_) {
			}
		},

		/** Subscribe to flag readiness from components via the store */
		onFlagsReady(cb) {
			if (this.flagsReady) {
				try {
					cb(this.flags);
				} catch (_) {
				}
				return;
			}
			_readyCallbacks.push(cb);
		},
	},
});
